/**
 * A strip that says this is the development portal — and renders **nothing at all** in production.
 *
 * <b>Why it exists.</b> Rasel, on giving the portal a second hostname: *"when he goes live, we need
 * to be able to differentiate whether we're logging into the production version or the development
 * version."* The hostname does most of that work, but the address bar is the first thing to
 * disappear: a muted tab, a bookmark, a screenshot pasted into a bug report, a phone in landscape.
 * The surest signal lives inside the page.
 *
 * <b>Why production renders nothing, rather than a green "Production" badge.</b> A banner announces
 * an EXCEPTION; it never narrates the default. That rule was learned here the expensive way — a
 * permanent "Not assigned" strip on the conversation detail screen, announcing the absence of
 * something that is absent nearly always, while the list screen next to it already did it correctly.
 * A production badge would be that mistake again, on every screen, for every customer, forever.
 *
 * <b>Why it names the API and not just the environment.</b> "Development" tells you which build you
 * are looking at. The API base tells you which DATA you are about to change, which is the question
 * someone actually has before they click something destructive. It is also the one thing that could
 * silently be wrong: a dev build pointed at the production API would be far more dangerous than no
 * ribbon at all, so the ribbon shows the value rather than asserting it is correct.
 *
 * <b>It is not dismissible.</b> A dismissible warning is a warning that is off precisely when
 * somebody has been working long enough to forget.
 */

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT as string | undefined;
const API_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined;

/** `staging` reads as a place; the host is what identifies it. Keeps the strip short. */
function shortApi(url: string | undefined): string {
  if (!url) return 'the same origin';
  try {
    const host = new URL(url).hostname;
    return host.startsWith('ca-fig-api-staging') ? 'staging API'
      : host.startsWith('ca-fig-api') ? 'PRODUCTION API'
      : host;
  } catch {
    return url;
  }
}

export function EnvironmentRibbon() {
  if (ENVIRONMENT !== 'development') return null;

  // If a development build is somehow pointed at production, that is not a footnote - it is the
  // most important thing on the screen, and the ribbon says so instead of reading as routine.
  const api = shortApi(API_BASE);
  const misdirected = api === 'PRODUCTION API';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        // Not `position: fixed`. A fixed strip overlays content at small heights and covers the
        // very thing the person is trying to read; in the normal flow it simply pushes the app down.
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
        padding: '0.4rem 1rem',
        font: '500 0.8125rem/1.4 var(--fig-font-sans, system-ui, sans-serif)',
        letterSpacing: '0.01em',
        textAlign: 'center',
        // Deliberately not a brand colour. It should read as scaffolding rather than as part of the
        // product, and it must not collide with the per-tenant theming the portal already applies.
        background: misdirected ? '#7f1d1d' : '#3f3f46',
        color: '#ffffff',
        // The one place a hard-coded colour pair is safe: both are fixed, and the contrast is
        // 12.6:1 (zinc-700 on white) and 10.9:1 (red-900 on white) - well past WCAG AA either way.
      }}
    >
      <strong style={{ fontWeight: 700 }}>
        {misdirected ? 'DEVELOPMENT BUILD — POINTED AT PRODUCTION' : 'Development'}
      </strong>
      <span style={{ opacity: 0.85 }}>
        {misdirected
          ? 'Anything you change here changes real customer data.'
          : `This is not the live portal. Connected to the ${api}.`}
      </span>
    </div>
  );
}
